"use client";

import { useEffect, useState, useMemo } from 'react';
import type { Location } from '@/types';
import { suggestPhotoOpportunity, SuggestPhotoOpportunityOutput } from '@/ai/flows/suggest-photo-opportunity';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Lightbulb } from 'lucide-react';

interface PhotoSuggestionProps {
  location: Location;
}

export default function PhotoSuggestion({ location }: PhotoSuggestionProps) {
  const [suggestion, setSuggestion] = useState<SuggestPhotoOpportunityOutput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timeOfDay = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const getSuggestion = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await suggestPhotoOpportunity({ location: location.name, timeOfDay });
        if (!isCancelled) {
          setSuggestion(result);
        }
      } catch (e) {
        if (!isCancelled) {
          setError("Couldn't get a suggestion. Please try again later.");
          console.error(e);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    getSuggestion();

    return () => {
      isCancelled = true;
    };
  }, [location, timeOfDay]);

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="font-headline flex items-center gap-2">
            <Lightbulb className="text-accent"/>
            Captain's Log
        </CardTitle>
        <CardDescription>AI-powered photo opportunity suggestion</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 min-h-[120px]">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : error ? (
            <p className="text-destructive">{error}</p>
        ) : suggestion ? (
          <>
            <h4 className="font-bold text-primary">{suggestion.suggestion}</h4>
            <p className="text-muted-foreground text-sm">{suggestion.reason}</p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
